<template>
    <div class="row banner-text">
        <div class="col-lg-6 offset-lg-3 col-xs-12">
            <div class="card">
                <div class="card-body">
                    <validation-observer
                        slim
                        ref="form"
                        v-slot="{ handleSubmit, invalid }"
                    >
                        <b-form
                            id="skin-form"
                            @submit.prevent="handle(handleSubmit, invalid)"
                            novalidate
                        >
                            <h1 class="text-center">
                                {{ $t("pages.forms.titles.skin-og01") }}
                            </h1>

                            <b-alert
                                v-model="showTopBannerForSeconds"
                                class="position-fixed fixed-top m-0 rounded-0"
                                style="z-index: 2000;"
                                variant="danger"
                                dismissible
                            >
                                <h4 class="mb-0">
                                    <span
                                        class="text-danger font-weight-bolder font-italic"
                                        >{{
                                            $t(
                                                "pages.forms.alerts.red-fields.banner-text"
                                            )
                                        }}</span
                                    >
                                </h4>
                            </b-alert>

                            <b-alert variant="info" :show="inReview">
                                <h4>
                                    <i class="fas fa-bell" />
                                    {{ $t("pages.forms.alerts.review.title") }}
                                </h4>
                                <p>
                                    {{
                                        $t(
                                            "pages.forms.alerts.review.description"
                                        )
                                    }}
                                </p>
                            </b-alert>

                            <h2 id="form-start">
                                {{ $t("pages.forms.headers.general") }}
                            </h2>

                            <og-sample-id
                                v-model="form.sampleId"
                                required
                                :reviewing="inReview"
                            />

                            <gender
                                v-model="form.gender"
                                required
                                :in-review="inReview"
                            />
                            <birth-date
                                v-model="form.birthdate"
                                required
                                :reviewing="inReview"
                            />
                            <h2>
                                {{
                                    $t("pages.forms.headers.skin.derm-history")
                                }}
                            </h2>

                            <ethnic-group
                                v-model="form.ethnicGroup"
                                required
                                :reviewing="inReview"
                            />
                            <foto-type
                                v-model="form.fotoType"
                                required
                                :reviewing="inReview"
                            />
                            <og-skin-type
                                v-model="form.skinType"
                                required
                                :reviewing="inReview"
                            />
                            <cutaneous-condition
                                v-model="form.cutaneousCondition"
                                required
                                :reviewing="inReview"
                            />

                            <h2>
                                {{
                                    $t(
                                        "pages.forms.headers.skin.skin-treatment"
                                    )
                                }}
                            </h2>

                            <og-cosmetic-treatment
                                v-model="form.cosmeticTreatment"
                                required
                                :reviewing="inReview"
                            />
                            <allergy-to-treatment
                                v-model="form.allergyToTreatment"
                                required
                                :reviewing="inReview"
                            />

                            <h2>{{ $t("pages.forms.headers.lifestyle") }}</h2>

                            <height
                                v-model="form.height"
                                required
                                :reviewing="inReview"
                            />
                            <weight
                                v-model="form.weight"
                                required
                                :reviewing="inReview"
                            />
                            <phy-act
                                v-model="form.phyAct"
                                required
                                :reviewing="inReview"
                            />
                            <sport-routine
                                v-model="form.sportRoutine"
                                required
                                :reviewing="inReview"
                            />

                            <yes-no-question
                                required
                                id="healthyDiet"
                                v-model="form.healthyDiet"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{ $t("pages.forms.skin.healthy-diet")
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <yes-no-question
                                required
                                id="healthyRest"
                                v-model="form.healthyRest"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{ $t("pages.forms.skin.healthy-rest")
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <yes-no-question
                                required
                                id="stress"
                                v-model="form.stress"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{ $t("pages.forms.skin.stress")
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <smoke
                                v-model="form.smoke"
                                required
                                :reviewing="inReview"
                            />

                            <alcohol
                                v-model="form.alcohol"
                                required
                                :reviewing="inReview"
                            />

                            <yes-no-question
                                required
                                id="waterConsumption"
                                v-model="form.waterConsumption"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{
                                        $t(
                                            "pages.forms.skin.water-consumption"
                                        )
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <yes-no-question
                                required
                                id="vitSupplements"
                                v-model="form.vitSupplements"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{ $t("pages.forms.skin.vit-supplements")
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <h2>
                                {{
                                    $t("pages.forms.headers.skin.sun-exposure")
                                }}
                            </h2>

                            <yes-no-question
                                required
                                id="sunTanning"
                                v-model="form.sunTanning"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{ $t("pages.forms.skin.sun-tanning")
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <yes-no-question
                                required
                                id="sunTanningCabine"
                                v-model="form.sunTanningCabine"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{
                                        $t(
                                            "pages.forms.skin.sun-tanning-cabine"
                                        )
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <sun-screen
                                v-model="form.sunscreen"
                                required
                                :reviewing="inReview"
                            />

                            <h2>
                                {{ $t("pages.forms.headers.medical-hist") }}
                            </h2>

                            <diseases
                                v-model="form.diseases"
                                required
                                :reviewing="inReview"
                            />
                            <drug-consumption
                                v-model="form.drugConsumption"
                                required
                                :in-review="inReview"
                            />
                            <allergies
                                v-model="form.allergies"
                                required
                                :in-review="inReview"
                            />
                            <gdpr-checkbox-og v-model="conditionsAccepted" />

                            <b-button-toolbar
                                style="justify-content: center; display: flex;"
                            >
                                <b-button-group v-show="inReview" class="mx-1">
                                    <b-button
                                        type="button"
                                        :disabled="alreadySent"
                                        @click.prevent="inReview = false"
                                        >{{ backButtonText }}</b-button
                                    >
                                </b-button-group>
                                <b-button-group class="mx-1">
                                    <b-button
                                        id="sendBtn"
                                        type="submit"
                                        variant="info"
                                        style="color: white;"
                                        :disabled="
                                            !conditionsAccepted || alreadySent
                                        "
                                    >
                                        <b-spinner
                                            small
                                            type="border"
                                            v-show="showSpinner"
                                        />
                                        {{ buttonText }}
                                    </b-button>
                                </b-button-group>
                            </b-button-toolbar>
                        </b-form>
                    </validation-observer>
                    <a
                        href="https://onegen01.s3-eu-west-1.amazonaws.com/kit-instructions.pdf"
                        target="_blank"
                        class="nav-link"
                        >{{ $t("pages.forms.common.kit-instructions") }}</a
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VueScrollTo from "vue-scrollto";
import FormInputHelpers from "../../mixins/FormInputHelpers";
import { COSMETIC_TREATMENT_TYPE_OTHER } from "../../components/form-components/skin/treatment/CosmeticTreatment";
import { DISEASE_OTHER } from "../../components/form-components/skin/medicalhist/Diseases";
import axios from "axios";
import { ValidationObserver } from "vee-validate";

export default {
    mixins: [FormInputHelpers],
    components: { ValidationObserver },
    data() {
        return {
            form: {
                sampleId: "",
                birthdate: "",
                ethnicGroup: null,
                fotoType: null,
                skinType: [],
                cutaneousCondition: [],
                cosmeticTreatment: {
                    cosmeticTreatment: null,
                    cosmeticTreatmentType: [],
                    cosmeticTreatmentOther: null
                },
                allergyToTreatment: {
                    allergyToTreatment: null,
                    allergyToTreatmentText: null
                },
                height: "",
                weight: "",
                phyAct: null,
                sportRoutine: {
                    sportRoutine: null,
                    sportIntensity: null
                },
                healthyDiet: "",
                healthyRest: "",
                stress: "",
                smoke: {
                    smoke: null,
                    smokeFrequency: null
                },
                alcohol: {
                    alcoholConsumption: null,
                    alcoholFrequency: null
                },
                waterConsumption: "",
                vitSupplements: "",
                sunTanning: "",
                sunTanningCabine: "",
                sunscreen: {
                    useSunscreen: null,
                    sunscreenFrequency: null
                },
                diseases: {
                    diseases: [],
                    otherDiseases: null
                },
                drugConsumption: {
                    drugConsumption: null,
                    drugConsumptionText: null
                },
                allergies: {
                    allergies: null,
                    allergiesText: null
                }
            },
            conditionsAccepted: false,
            inReview: false,
            showSpinner: false,
            alreadySent: false,
            showTopBannerForSeconds: false,
            buttonText: this.$t("pages.forms.button-texts.next"),
            backButtonText: this.$t("pages.forms.button-texts.back")
        };
    },
    methods: {
        handle(handleSubmit, invalid) {
            if (invalid) {
                this.showTopBannerForSeconds = 5;
            }
            handleSubmit(this.submit);
        },
        async submit(e) {
            if (!this.inReview) {
                this.inReview = true;
                VueScrollTo.scrollTo(document.getElementById("skin-form"));
                this.buttonText = this.$i18n.t("pages.forms.button-texts.send");
                return;
            }

            try {
                this.buttonText = this.$i18n.t(
                    "pages.forms.button-texts.sending-data"
                );
                this.showSpinner = true;

                await axios.post(
                    this.route("onegen01.register.skin_response"),
                    {
                        sampleID: "OG" + this.form.sampleId,
                        birthdate: this.form.birthdate,
                        gender: this.form.gender,
                        Height: this.form.height,
                        Weight: this.form.weight,
                        phyAct: this.form.phyAct.value,
                        sportRoutine: this.form.sportRoutine.sportRoutine,
                        sportIntensity:
                            this.form.sportRoutine.sportRoutine === 1
                                ? this.form.sportRoutine.sportIntensity.value
                                : null,
                        ethnicGroup: this.form.ethnicGroup.value,
                        fotoType: this.form.fotoType.value,
                        skinType: this.form.skinType.map(t => t.value),
                        cutaneousCondition: this.form.cutaneousCondition.map(
                            t => t.value
                        ),
                        cosmeticTreatment: this.form.cosmeticTreatment
                            .cosmeticTreatment,
                        cosmeticTreatmentType:
                            this.form.cosmeticTreatment.cosmeticTreatment === 1
                                ? this.form.cosmeticTreatment.cosmeticTreatmentType.map(
                                      t => t.value
                                  )
                                : null,
                        cosmeticTreatmentOther: this.form.cosmeticTreatment.cosmeticTreatmentType
                            .map(v => v.value)
                            .includes(COSMETIC_TREATMENT_TYPE_OTHER)
                            ? this.form.cosmeticTreatment.cosmeticTreatmentOther
                            : null,
                        allergyToTreatment: this.form.allergyToTreatment
                            .allergyToTreatment,
                        allergyToTreatmentText:
                            this.form.allergyToTreatment.allergyToTreatment ===
                            1
                                ? this.form.allergyToTreatment
                                      .allergyToTreatmentText
                                : null,
                        healthyDiet: this.form.healthyDiet,
                        healthyRest: this.form.healthyRest,
                        stress: this.form.stress,
                        smoke: this.form.smoke.smoke,
                        smokeFrequency:
                            this.form.smoke.smoke === 1
                                ? this.form.smoke.smokeFrequency.value
                                : null,
                        alcoholConsumption: this.form.alcohol
                            .alcoholConsumption,
                        alcoholFrequency:
                            this.form.alcohol.alcoholConsumption === 1
                                ? this.form.alcohol.alcoholFrequency.value
                                : null,
                        waterConsumption: this.form.waterConsumption,
                        vitSupplements: this.form.vitSupplements,
                        sunTanning: this.form.sunTanning,
                        sunTanningCabine: this.form.sunTanningCabine,
                        useSunscreen: this.form.sunscreen.useSunscreen,
                        sunscreenFrequency:
                            this.form.sunscreen.useSunscreen === 1
                                ? this.form.sunscreen.sunscreenFrequency.value
                                : null,
                        diseases: this.form.diseases.diseases.map(v => v.value),
                        diseasesOther: this.form.diseases.diseases
                            .map(v => v.value)
                            .includes(DISEASE_OTHER)
                            ? this.form.diseases.otherDiseases
                            : null,
                        drugConsumption: this.form.drugConsumption
                            .drugConsumption,
                        drugConsumptionText:
                            this.form.drugConsumption.drugConsumption === 1
                                ? this.form.drugConsumption.drugConsumptionText
                                : null,
                        allergies: this.form.allergies.allergies,
                        allergiesText: this.form.allergies.allergiesText
                    }
                );

                this.alreadySent = true;

                this.$swal({
                    title: this.$i18n.t(
                        "pages.forms.alerts.swal.success.title"
                    ),
                    text: this.$i18n.t("pages.forms.alerts.swal.success.text"),
                    icon: "success",
                    showCancelButton: false,
                    timer: 6000
                }).then(function() {
                    window.location.replace("https://register.onegen01.com");
                });
            } catch (e) {
                console.log(e);

                this.alreadySent = false;
                this.buttonText = this.$i18n.t("pages.forms.button-texts.next");
                this.showSpinner = false;

                if (e.response.status === 404) {
                    this.$swal({
                        title: this.$i18n.t(
                            "pages.forms.alerts.swal.error.not-exist.title"
                        ),
                        text: this.$i18n.t(
                            "pages.forms.alerts.swal.error.not-exist.text"
                        ),
                        icon: "error",
                        confirmButtonColor: "#DD6B55"
                    });
                } else if (e.response.status === 409) {
                    this.$swal({
                        title: this.$i18n.t(
                            "pages.forms.alerts.swal.error.form-exist.title"
                        ),
                        text: this.$i18n.t(
                            "pages.forms.alerts.swal.error.form-exist.text"
                        ),
                        icon: "error",
                        confirmButtonColor: "#DD6B55"
                    });
                } else {
                    this.$swal({
                        title: this.$i18n.t(
                            "pages.forms.alerts.swal.error.other.title"
                        ),
                        text: this.$i18n.t(
                            "pages.forms.alerts.swal.error.other.text"
                        ),
                        icon: "error",
                        confirmButtonColor: "#DD6B55"
                    });
                }
            }
        }
    }
};
</script>

<style scoped></style>
